import React, { useMemo } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { theme } from '../../../../../shared/theme';
import { ExperienceType } from '../../../ExperienceReviews/ReviewWizard';
import DriverReservationDetails from '../DriverReservationDetails';
import { BookingSetMenu } from '../../../../types/set-menu';
import { replaceSpacesWithHyphens } from '../../../../../shared/utils';
import UserRole from '../../../../../shared/AuthRoles';
import GuestReviewScoreHint from '../../../Reviews/GuestReviewScoreHint';
import { Job } from '../../../../../app/Slices/SevenDayApiTypes';
import {
    useGetGuestProfileQuery,
    useGetGuestUserProfileQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';

type Props = {
    reservation: any;
    asset: any;
    fullAddress: string;
    shortAddress: string;
    fullAddressDropoff: string;
    expanded: boolean;
    onToggle: (event: React.SyntheticEvent, isExpanded: boolean) => void;
    cateringJobs?: Job[];
    alcoholJobs?: Job[];
    massageJobs?: Job[];
    cateringSetMenuJobs?: BookingSetMenu[];
    alcoholSetMenuJobs?: BookingSetMenu[];
    onOpenChargesTab?: (target?: 'catering' | 'massage') => void;
    handleOpenMainBooking: (bookingId: number) => void;
};

export const LinkSx = {
    color: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    transition: 'color 0.2s ease',
    '&:hover': { color: 'secondary.main' },
};

export default function ReservationDetailsSection({
    reservation,
    asset,
    fullAddress,
    shortAddress,
    fullAddressDropoff,
    expanded,
    onToggle,
    cateringJobs = [],
    alcoholJobs = [],
    massageJobs = [],
    cateringSetMenuJobs = [],
    alcoholSetMenuJobs = [],
    onOpenChargesTab,
    handleOpenMainBooking,
}: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isOwnerBooking =
        String(reservation?.bookerType || '').toLowerCase() === 'owner';
    const isOwnerBlock = isOwnerBooking && reservation?.onBehalf === false;
    const isOwnerOnBehalfOf = isOwnerBooking && reservation?.onBehalf === true;

    const hasMassages = asset?.details?.massages;
    const showMassages = Boolean(hasMassages) && !isOwnerBlock;

    const showCateringAlcoholSection =
        reservation?.module?.type !== 'activity' &&
        reservation?.module?.type !== 'vehicle' &&
        !isOwnerBlock;

    const hasCateringJobs =
        cateringJobs.length + cateringSetMenuJobs.length > 0;
    const hasMassageJobs = massageJobs.length > 0;

    const cateringJobCount = cateringJobs.length + cateringSetMenuJobs.length;
    const alcoholJobCount = alcoholJobs.length + alcoholSetMenuJobs.length;
    const massageJobCount = massageJobs.length;

    const getCateringText = () => {
        const requires = reservation?.bookingInfo?.requiresCatering;
        const notes = reservation?.bookingInfo?.cateringNotes;
        const hasJobs = cateringJobs?.length + cateringSetMenuJobs?.length > 0;

        if (requires) {
            if (hasJobs) return 'Charges available';
            if (notes) return notes;
            return 'No catering details provided';
        }

        if (hasJobs) return 'Charges available';
        return 'No catering requested';
    };
    const getAlcoholText = () => {
        const requires = reservation?.bookingInfo?.requiresBeverages;
        const notes = reservation?.bookingInfo?.beverageNotes;
        const hasJobs = alcoholSetMenuJobs?.length + alcoholJobs?.length > 0;

        if (requires) {
            if (hasJobs) return 'Charges available';
            if (notes) return notes;
            return 'No alcohol details provided';
        }

        if (hasJobs) return 'Charges available';
        return 'No alcohol requested';
    };

    const getMassageText = () => {
        const requires = reservation?.bookingInfo?.requiresMassages;
        const notes = reservation?.bookingInfo?.massageNotes;
        const hasJobs = massageJobs?.length > 0;

        if (requires) {
            if (hasJobs) return 'Charges available';
            if (notes) return notes;
            return 'No wellness service request details provided';
        }

        if (hasJobs) return 'Charges available';
        return 'No wellness service requested';
    };

    const cardSx = {
        borderRadius: 2,
        py: 1.5,
        mb: 2,
    };

    const labelSx = {
        fontWeight: 700,
        lineHeight: {
            xs: 1.35,
            md: 1.75,
        },
        color: theme.palette.text.secondary,
    };

    const valueSx = {
        color: theme.palette.text.primary,
        lineHeight: {
            xs: 1.35,
            md: 1.75,
        },
        wordBreak: 'break-word' as const,
    };

    const fieldsGridSx = {
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        columnGap: isMobile ? 1.5 : 0,
        rowGap: isMobile ? 2 : 1.25,
        width: '100%',
    };

    const DetailField = ({
        label,
        children,
        fullWidth = false,
    }: {
        label: React.ReactNode;
        children: React.ReactNode;
        fullWidth?: boolean;
    }) => {
        const labelId =
            typeof label === 'string'
                ? replaceSpacesWithHyphens(label)
                : undefined;

        return (
            <Box
                sx={{
                    gridColumn: isMobile && fullWidth ? '1 / -1' : undefined,
                    display: 'grid',
                    gridTemplateColumns: isMobile
                        ? '1fr'
                        : 'minmax(150px, 210px) 1fr',
                    columnGap: isMobile ? 0 : 3,
                    rowGap: isMobile ? 0.25 : 0.5,
                    minWidth: 0,
                }}
            >
                <Typography variant='body1' sx={labelSx} id={labelId}>
                    {label}
                </Typography>
                <Box sx={valueSx} aria-labelledby={labelId}>
                    {children}
                </Box>
            </Box>
        );
    };

    const ValueLink = ({
        to,
        children,
    }: {
        to: string;
        children: React.ReactNode;
    }) => (
        <Box
            component='a'
            href={to}
            target='_blank'
            rel='noreferrer'
            sx={LinkSx}
        >
            {children}
        </Box>
    );

    const ExperienceLink = ({
        type,
        id,
        name,
    }: {
        type: string;
        id: string | number;
        name: string;
    }) => (
        <Box
            component={RouterLink}
            to={`/viewExperiences/${type}/${id}`}
            sx={LinkSx}
        >
            {name}
        </Box>
    );

    const notesValue = isOwnerBlock
        ? reservation?.notes
        : reservation?.bookingInfo?.notes;

    const hasLinked =
        (reservation?.linkedModules?.length || 0) > 0 ||
        Boolean(asset?.mainModule);

    const linkedTarget = reservation?.linkedModules?.[0] || asset?.mainModule;

    const showRequestsSection =
        !isOwnerBlock &&
        ((showCateringAlcoholSection &&
            (asset?.details?.catering || asset?.details?.alcohol)) ||
            (reservation?.module?.type === 'property' && showMassages));

    const outerAccordionSx = {
        mt: 2,
        borderRadius: 2,
        boxShadow: 'none',
        '&::before': { display: 'none' },
        overflow: 'hidden',
    };

    const companyName =
        reservation?.company?.name ||
        reservation?.companyName ||
        reservation?.guest?.companyName ||
        reservation?.guest?.company?.name ||
        reservation?.booker?.companyName ||
        reservation?.booker?.company?.name ||
        reservation?.owner?.companyName ||
        reservation?.owner?.company?.name ||
        reservation?.guest?.name ||
        reservation?.owner?.name ||
        '';

    const loggedInUser = UserRole();
    const isOwnerUser = loggedInUser?.role?.includes('owner');
    const canViewGuestProfiles =
        isOwnerUser && Boolean(reservation?.guest?.id) && !isOwnerBooking;

    const { data: guestCompanyProfile } = useGetGuestProfileQuery(
        reservation?.guest?.id,
        { skip: !canViewGuestProfiles || !reservation?.guest?.id },
    );
    const { data: primaryGuestProfile } = useGetGuestUserProfileQuery(
        reservation?.booker?.id,
        {
            skip:
                !canViewGuestProfiles ||
                !reservation?.booker?.id ||
                isOwnerOnBehalfOf,
        },
    );

    const companyLabel = useMemo(() => {
        const onBehalf = Boolean(reservation?.onBehalf);

        if (isOwnerBooking && onBehalf)
            return `Booked on behalf of ${companyName || '—'}`;
        if (isOwnerBooking) return `Blocked by ${companyName || '—'}`;
        return companyName || '—';
    }, [isOwnerBooking, reservation?.onBehalf, companyName]);

    const companyReviewHint = (
        <GuestReviewScoreHint
            averageRating={guestCompanyProfile?.averageRating}
            reviewCount={guestCompanyProfile?.reviewCount}
        />
    );

    const hasCompanyReviews = Boolean(guestCompanyProfile?.reviewCount);
    const companyValue =
        isOwnerUser && reservation?.guest?.id && !isOwnerBooking ? (
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.75,
                }}
            >
                {hasCompanyReviews ? (
                    <Box
                        component={RouterLink}
                        to={`/guests/${reservation.guest.id}`}
                        sx={LinkSx}
                    >
                        {companyLabel}
                    </Box>
                ) : (
                    companyLabel
                )}
                {companyReviewHint}
            </Box>
        ) : (
            companyLabel
        );

    const reservationReason =
        reservation?.bookingInfo?.reservationReason ||
        reservation?.reservationReason ||
        reservation?.reason ||
        reservation?.notes ||
        '—';

    const isVehicleBooking = reservation?.module?.type === 'vehicle';
    const showDriverSection =
        isVehicleBooking &&
        (Boolean(reservation?.onBehalf) || reservation?.bookerType !== 'owner');

    const drivers = reservation?.bookingInfo?.drivers || [];

    const locationLabel =
        reservation?.module?.type === 'vehicle'
            ? 'Pickup location'
            : 'Location';

    const locationValue = reservation?.module?.isAvailable ? (
        <ValueLink
            to={`https://www.google.com/maps/place/${encodeURIComponent(
                shortAddress,
            )}`}
        >
            {shortAddress}
        </ValueLink>
    ) : (
        'This experience is no longer available'
    );

    const experienceValue = (
        <>
            {reservation?.module?.isAvailable ? (
                <ExperienceLink
                    type={reservation?.module?.type}
                    id={reservation?.module?.id}
                    name={reservation?.module?.name}
                />
            ) : (
                <span>{reservation?.module?.name}</span>
            )}{' '}
            – hosted by {reservation?.owner?.name}
        </>
    );

    const mobilePairGridSx = {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        columnGap: 1.5,
    };

    const showPrimaryGuest = !isOwnerBlock && !isOwnerOnBehalfOf;

    const primaryGuestName = [
        reservation?.booker?.firstName,
        reservation?.booker?.lastName,
    ]
        .filter(Boolean)
        .join(' ');

    const primaryGuestReviewHint = (
        <GuestReviewScoreHint
            averageRating={primaryGuestProfile?.averageRating}
            reviewCount={primaryGuestProfile?.reviewCount}
        />
    );

    const primaryGuestEmail = reservation?.booker?.email
        ? ` – ${reservation.booker.email}`
        : '';

    const hasPrimaryGuestReviews = Boolean(primaryGuestProfile?.reviewCount);
    const primaryGuestValue =
        isOwnerUser && reservation?.booker?.id && !isOwnerBooking ? (
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.75,
                }}
            >
                {hasPrimaryGuestReviews ? (
                    <Box
                        component={RouterLink}
                        to={`/guest-users/${reservation.booker.id}`}
                        sx={LinkSx}
                    >
                        {primaryGuestName}
                    </Box>
                ) : (
                    primaryGuestName
                )}
                {primaryGuestEmail}
                {primaryGuestReviewHint}
            </Box>
        ) : (
            <>
                {primaryGuestName}
                {primaryGuestEmail}
            </>
        );

    const reservationMadeByValue = (
        <>
            {reservation?.createdBy?.firstName}{' '}
            {reservation?.createdBy?.lastName} – {reservation?.createdBy?.email}
        </>
    );

    const visitTypeValue = reservation?.bookingInfo?.visitType
        ? reservation.bookingInfo.visitType.charAt(0).toUpperCase() +
          reservation.bookingInfo.visitType.slice(1)
        : '—';

    const showCatering =
        showCateringAlcoholSection && Boolean(asset?.details?.catering);
    const showAlcohol =
        showCateringAlcoholSection && Boolean(asset?.details?.alcohol);

    const cateringValueContent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {getCateringText()}

            {hasCateringJobs && onOpenChargesTab ? (
                <Button
                    variant='outlined'
                    size='medium'
                    onClick={() => onOpenChargesTab('catering')}
                    sx={{
                        alignSelf: 'flex-start',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '10px',
                    }}
                >
                    View catering charges ({cateringJobCount})
                </Button>
            ) : null}
        </Box>
    );

    const alcoholValueContent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {getAlcoholText()}

            {alcoholJobCount > 0 && onOpenChargesTab ? (
                <Button
                    variant='outlined'
                    size='medium'
                    onClick={() => onOpenChargesTab('catering')}
                    sx={{
                        alignSelf: 'flex-start',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '10px',
                    }}
                >
                    View alcohol charges ({alcoholJobCount})
                </Button>
            ) : null}
        </Box>
    );

    return (
        <Accordion
            component='section'
            aria-labelledby='reservation-details-heading'
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            square={false}
            sx={outerAccordionSx}
        >
            <AccordionSummary
                aria-controls='reservation-details-content'
                id='reservation-details-header'
                sx={{
                    py: 1.25,
                    backgroundColor: theme.palette.neutral.main,
                    px: 0,
                }}
            >
                <Typography
                    id='reservation-details-heading'
                    component='h2'
                    variant='h2'
                    sx={{ letterSpacing: 0.2, color: 'text.secondary' }}
                >
                    Reservation Details
                </Typography>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    px: 0,
                    pb: isMobile ? 2 : 3,
                    pt: 0,
                    backgroundColor: theme.palette.neutral.main,
                }}
            >
                <Divider sx={{ mt: 0, mb: 2 }} />

                <Box sx={cardSx}>
                    <Box sx={fieldsGridSx}>
                        {isMobile ? (
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.25,
                                }}
                            >
                                <Box sx={mobilePairGridSx}>
                                    <Typography variant='body1' sx={labelSx}>
                                        Experience
                                    </Typography>
                                    <Typography variant='body1' sx={labelSx}>
                                        {locationLabel}
                                    </Typography>
                                </Box>
                                <Box sx={mobilePairGridSx}>
                                    <Box sx={valueSx}>{experienceValue}</Box>
                                    <Box sx={valueSx}>{locationValue}</Box>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <DetailField label='Experience'>
                                    {experienceValue}
                                </DetailField>

                                <DetailField label='Type'>
                                    {reservation?.module?.type
                                        ? reservation?.module?.type
                                              .charAt(0)
                                              .toUpperCase() +
                                          reservation?.module?.type.slice(1)
                                        : '—'}
                                </DetailField>
                            </>
                        )}

                        {hasLinked ? (
                            <DetailField label='Linked to' fullWidth>
                                {linkedTarget?.type && linkedTarget?.id ? (
                                    <ExperienceLink
                                        type={linkedTarget.type}
                                        id={linkedTarget.id}
                                        name={linkedTarget.name}
                                    />
                                ) : (
                                    <span>{linkedTarget?.name}</span>
                                )}{' '}
                                - Note: This experience is linked to another
                                experience owned by the host. When making a
                                booking for this experience, it will{' '}
                                <strong>
                                    {reservation.linkedModules[0].linkType ===
                                    'block'
                                        ? 'block'
                                        : 'combine'}
                                </strong>{' '}
                                {reservation.linkedModules[0].linkType ===
                                'block'
                                    ? 'the other experience. However, it does not combine the facilities and you will not be charged additional for this – '
                                    : 'the experiences and provide access to both facilities at no additional cost. The number of bedrooms/guests listed is the total across both experiences. '}
                            </DetailField>
                        ) : null}

                        {reservation?.mainBooking?.id ? (
                            <DetailField label='Itinerary' fullWidth>
                                This reservation is part of
                                <Box
                                    onClick={() =>
                                        handleOpenMainBooking(
                                            reservation?.mainBooking?.id,
                                        )
                                    }
                                    component='span'
                                    sx={{
                                        ...LinkSx,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {' '}
                                    {`Reservation ${reservation?.mainBooking?.id} Itinerary`}
                                </Box>{' '}
                            </DetailField>
                        ) : null}
                    </Box>
                </Box>

                {!isMobile || asset?.type === ExperienceType.Vehicle ? (
                    <Box sx={cardSx}>
                        <Box sx={fieldsGridSx}>
                            {!isMobile ? (
                                <DetailField label={locationLabel}>
                                    {locationValue}
                                </DetailField>
                            ) : null}

                            {asset?.type === ExperienceType.Vehicle ? (
                                <DetailField
                                    label='Dropoff location'
                                    fullWidth={isMobile}
                                >
                                    <ValueLink
                                        to={
                                            asset?.details
                                                ?.dropoffLocationSameAsPickup
                                                ? `https://www.google.com/maps/place/${encodeURIComponent(
                                                      fullAddress,
                                                  )}`
                                                : `https://www.google.com/maps/place/${encodeURIComponent(
                                                      fullAddressDropoff,
                                                  )}`
                                        }
                                    >
                                        {asset?.details
                                            ?.dropoffLocationSameAsPickup
                                            ? 'Same as pickup'
                                            : fullAddressDropoff}
                                    </ValueLink>
                                </DetailField>
                            ) : null}
                        </Box>
                    </Box>
                ) : null}

                <Box sx={fieldsGridSx}>
                    {!isMobile ? (
                        <DetailField label='Company'>
                            {companyValue}
                        </DetailField>
                    ) : null}

                    {showPrimaryGuest && isMobile ? (
                        <Box
                            sx={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                            }}
                        >
                            <Box sx={mobilePairGridSx}>
                                <Typography
                                    variant='body1'
                                    sx={labelSx}
                                    id='primary-guest'
                                >
                                    Primary guest
                                </Typography>
                                <Typography variant='body1' sx={labelSx}>
                                    Reservation made by
                                </Typography>
                            </Box>
                            <Box sx={mobilePairGridSx}>
                                <Box
                                    sx={valueSx}
                                    aria-labelledby='primary-guest'
                                >
                                    {primaryGuestValue}
                                </Box>
                                <Box sx={valueSx}>{reservationMadeByValue}</Box>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {showPrimaryGuest ? (
                                <DetailField label='Primary guest'>
                                    {primaryGuestValue}
                                </DetailField>
                            ) : null}

                            <DetailField label='Reservation made by'>
                                {reservationMadeByValue}
                            </DetailField>
                        </>
                    )}

                    {isOwnerBlock ? (
                        <>
                            {isMobile ? (
                                <DetailField label='Company' fullWidth>
                                    {companyValue}
                                </DetailField>
                            ) : null}

                            <DetailField label='Reservation reason' fullWidth>
                                {reservationReason}
                            </DetailField>
                        </>
                    ) : isMobile ? (
                        <Box
                            sx={{
                                ...cardSx,
                                gridColumn: '1 / -1',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                            }}
                        >
                            <Box sx={mobilePairGridSx}>
                                <Typography variant='body1' sx={labelSx}>
                                    Company
                                </Typography>
                                <Typography variant='body1' sx={labelSx}>
                                    Visit type
                                </Typography>
                            </Box>
                            <Box sx={mobilePairGridSx}>
                                <Box sx={valueSx}>{companyValue}</Box>
                                <Box sx={valueSx}>{visitTypeValue}</Box>
                            </Box>
                        </Box>
                    ) : (
                        <DetailField label='Visit type'>
                            {visitTypeValue}
                        </DetailField>
                    )}
                </Box>

                {showDriverSection ? (
                    <Box sx={cardSx}>
                        <Box sx={fieldsGridSx}>
                            <DetailField label='Driver information' fullWidth>
                                {drivers?.length ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        <DriverReservationDetails
                                            drivers={drivers}
                                        />
                                    </Box>
                                ) : (
                                    'No driver details provided'
                                )}
                            </DetailField>
                        </Box>
                    </Box>
                ) : null}

                {showRequestsSection ? (
                    <Box sx={cardSx}>
                        <Box sx={{ mt: 1 }}>
                            <Box sx={fieldsGridSx}>
                                {showCatering && showAlcohol && isMobile ? (
                                    <Box
                                        sx={{
                                            gridColumn: '1 / -1',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.25,
                                        }}
                                    >
                                        <Box sx={mobilePairGridSx}>
                                            <Typography
                                                variant='body1'
                                                sx={labelSx}
                                            >
                                                Catering
                                            </Typography>
                                            <Typography
                                                variant='body1'
                                                sx={labelSx}
                                            >
                                                Alcohol
                                            </Typography>
                                        </Box>
                                        <Box sx={mobilePairGridSx}>
                                            <Box sx={valueSx}>
                                                {cateringValueContent}
                                            </Box>
                                            <Box sx={valueSx}>
                                                {alcoholValueContent}
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : (
                                    <>
                                        {showCatering ? (
                                            <DetailField
                                                label='Catering'
                                                fullWidth={
                                                    isMobile && !showAlcohol
                                                }
                                            >
                                                {cateringValueContent}
                                            </DetailField>
                                        ) : null}

                                        {showAlcohol ? (
                                            <DetailField
                                                label='Alcohol'
                                                fullWidth={
                                                    isMobile && !showCatering
                                                }
                                            >
                                                {alcoholValueContent}
                                            </DetailField>
                                        ) : null}
                                    </>
                                )}

                                {reservation?.module?.type === 'property' &&
                                showMassages ? (
                                    <DetailField
                                        label='Wellness'
                                        fullWidth={
                                            isMobile &&
                                            !showCatering &&
                                            !showAlcohol
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                            }}
                                        >
                                            {getMassageText()}

                                            {hasMassageJobs &&
                                            onOpenChargesTab ? (
                                                <Button
                                                    onClick={() =>
                                                        onOpenChargesTab(
                                                            'massage',
                                                        )
                                                    }
                                                    variant='outlined'
                                                    size='small'
                                                    sx={{
                                                        alignSelf: 'flex-start',
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        fontSize: '10px',
                                                    }}
                                                >
                                                    View wellness charges (
                                                    {massageJobCount})
                                                </Button>
                                            ) : null}
                                        </Box>
                                    </DetailField>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                ) : null}

                {notesValue ? (
                    <Box sx={cardSx}>
                        <Box sx={{ mt: 1 }}>
                            <Box sx={fieldsGridSx}>
                                <DetailField label='Booking notes' fullWidth>
                                    {notesValue}
                                </DetailField>
                            </Box>
                        </Box>
                    </Box>
                ) : null}
            </AccordionDetails>
        </Accordion>
    );
}
